import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, useThemeProps } from '@mui/material'

import useToolbarInfo from '../../hooks/useToolbarInfo'
import theme from '../../theme/theme.mui'
import type { PageLayoutProps } from './PageLayout.types'
import PageLayoutContainer from './PageLayoutContainer'
import { PageLayoutRoot } from './PageLayout.styles'

/** Width of this layout's scrollbar; 0px where the platform uses overlay scrollbars */
export const PAGE_SCROLLBAR_WIDTH_VAR = '--page-scrollbar-width'

// Document-wide, so only the last layout to unmount clears it
let scrollbarWidthPublishers = 0

const PageLayout = (inProps: PageLayoutProps) => {
  const props = useThemeProps({ props: inProps, name: 'PageLayout' })
  const rootRef = useRef<HTMLElement>(null)
  const { shouldRenderGhost, height: toolbarHeight } = useToolbarInfo()
  const {
    variant,
    maxWidth,
    children,
    warningChildren,
    sidebar,
    sidebarOpen,
    sidebarWidth,
    sidebarCollapsedWidth,
    navHidden,
    contentBottomInset,
    publishScrollbarWidth = false,
    containerProps,
    ...boxProps
  } = props

  // Content should not transition on mount if the sidebar is already open
  const [shouldTransition, setShouldTransition] = useState(!sidebarOpen)

  useEffect(() => {
    if (shouldTransition) return

    const timeout = setTimeout(() => setShouldTransition(true), theme.transitions.duration.enteringScreen)

    return () => clearTimeout(timeout)
  }, [shouldTransition])

  useEffect(() => {
    const element = rootRef.current

    if (!element || !publishScrollbarWidth) return

    // Re-measured on resize because browser zoom changes the width in CSS pixels
    const publish = () =>
      document.documentElement.style.setProperty(
        PAGE_SCROLLBAR_WIDTH_VAR,
        `${element.offsetWidth - element.clientWidth}px`,
      )

    publish()
    scrollbarWidthPublishers += 1
    window.addEventListener('resize', publish)

    return () => {
      window.removeEventListener('resize', publish)
      scrollbarWidthPublishers -= 1

      if (scrollbarWidthPublishers === 0) {
        document.documentElement.style.removeProperty(PAGE_SCROLLBAR_WIDTH_VAR)
      }
    }
  }, [publishScrollbarWidth])

  const containerMaxWidth = useMemo(() => {
    if (navHidden) {
      return maxWidth ?? false
    } else if (variant === 'two-column' || variant === 'two-column-with-sidebar') {
      return 'lg'
    } else {
      return maxWidth ?? false
    }
  }, [maxWidth, navHidden, variant])

  return (
    <>
      <PageLayoutRoot
        ref={rootRef}
        component="main"
        variant={variant}
        sidebarOpen={sidebarOpen}
        sidebarWidth={sidebarWidth}
        sidebarCollapsedWidth={sidebarCollapsedWidth}
        navHidden={navHidden}
        contentBottomInset={contentBottomInset}
        shouldTransition={shouldTransition}
        shouldRenderGhost={shouldRenderGhost}
        {...boxProps}
      >
        <Box mt={shouldRenderGhost ? toolbarHeight : 0}>
          <Box>{warningChildren}</Box>
          <PageLayoutContainer
            variant={variant}
            disableGutters={containerProps?.disableGutters}
            maxWidth={containerMaxWidth}
            contentBottomInset={contentBottomInset}
          >
            <Box display="flex" flexDirection="column" width="100%">
              {children}
            </Box>
          </PageLayoutContainer>
        </Box>
      </PageLayoutRoot>
      {sidebar}
    </>
  )
}

export default PageLayout
